import React from 'react'
import { ChevronRight } from 'lucide-react'

import { DELIVERY_WORK_CATEGORY } from 'repositories/ShipmentApi/getDeliveryWorkTypeSettings/type'
import type {
  DeliveryWorkCategory,
  DeliveryWorkTypeSetting,
} from 'repositories/ShipmentApi/getDeliveryWorkTypeSettings/type'

import styles from './DeliveryWorkTypeChips.module.css'

type Props = {
  types: DeliveryWorkTypeSetting[]
  selectedIds: ReadonlySet<number>
  onToggle: (id: number) => void
  disabled: boolean
}

type ChipProps = {
  type: DeliveryWorkTypeSetting
  selected: boolean
  onToggle: (id: number) => void
  disabled: boolean
}

// カテゴリごとに displayOrder 昇順で返す（描画用。BE も同順で返すが表示保証として並べ直す）
const getTypesByCategory = (
  types: DeliveryWorkTypeSetting[],
  category: DeliveryWorkCategory
): DeliveryWorkTypeSetting[] =>
  types.filter((t) => t.category === category).sort((a, b) => a.displayOrder - b.displayOrder)

const Chip: React.FC<ChipProps> = ({ type, selected, onToggle, disabled }) => (
  <button
    type="button"
    className={`${styles.chip} ${selected ? styles.chipSelected : ''}`}
    aria-pressed={selected}
    disabled={disabled}
    onClick={() => onToggle(type.id)}
  >
    {type.name}
  </button>
)

// 付帯作業チップ群。汎用（GENERAL）はデフォルト表示、専門（SPECIALIZED）はアコーディオン折りたたみ（既定 closed）。
// NOTE: アコーディオンの開閉は表示のみ。選択状態は selectedIds（親）で保持するため、閉じても選択は維持される。
export const DeliveryWorkTypeChips: React.FC<Props> = ({
  types,
  selectedIds,
  onToggle,
  disabled,
}) => {
  const general = getTypesByCategory(types, DELIVERY_WORK_CATEGORY.GENERAL)
  const specialized = getTypesByCategory(types, DELIVERY_WORK_CATEGORY.SPECIALIZED)

  // サマリ右端のヒント（例: ドラム回し / 玉掛け ほか）
  const hint =
    specialized.length > 0
      ? `${specialized
          .slice(0, 2)
          .map((t) => t.name)
          .join(' / ')}${specialized.length > 2 ? ' ほか' : ''}`
      : ''

  return (
    <div className={styles.container}>
      <div className={styles.heading} data-dg-id="sp-delivery-work-types-title">
        付帯作業<span className={styles.headingNote}>（複数選択可・タップで選択）</span>
      </div>

      <div className={styles.chips}>
        {general.map((type) => (
          <Chip
            key={type.id}
            type={type}
            selected={selectedIds.has(type.id)}
            onToggle={onToggle}
            disabled={disabled}
          />
        ))}
      </div>

      {specialized.length > 0 && (
        <details className={styles.accordion}>
          <summary className={styles.accordionSummary}>
            <ChevronRight className={styles.accordionIcon} size={18} aria-hidden={true} />
            専門作業を表示
            {hint && <span className={styles.accordionHint}>{hint}</span>}
          </summary>
          <div className={styles.accordionChips}>
            {specialized.map((type) => (
              <Chip
                key={type.id}
                type={type}
                selected={selectedIds.has(type.id)}
                onToggle={onToggle}
                disabled={disabled}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  )
}
